"use client";

import { useEffect, useRef, useState } from "react";
import { Star, Quote } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ClientTestimonials() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const testimonials = [{
    id: 1,
    name: "Michael Anderson",
    role: "CEO, Tech Innovations",
    text: "Phoenix Enterprise TCP provided exceptional chauffeur service for our executive team. The professionalism and luxury vehicles exceeded all expectations.",
    rating: 5
  }, {
    id: 2,
    name: "Sarah Jenkins",
    role: "Event Coordinator",
    text: "The most reliable VIP transportation in Los Angeles. Their attention to detail and punctuality made our high-profile gala run perfectly smooth.",
    rating: 5
  }, {
    id: 3,
    name: "David Chen",
    role: "Frequent Traveler",
    text: "I use their airport transfer service weekly. Discreet, professional, and always on time. The black SUV fleet is impeccably maintained.",
    rating: 5
  }];
  return <section id="client-testimonials" ref={sectionRef} className="dark bg-dark-section text-dark-foreground section-padding relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto space-y-16">
          <div className="text-center space-y-4 animate-reveal" data-index={0}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight">
              Client Testimonials
            </h2>
            <div className="w-24 h-1 bg-secondary mx-auto rounded-full" />
            <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-sans">
              Discover why professionals trust Phoenix Enterprises Limousine Service for luxury chauffeur service in Los Angeles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => <TestimonialCard key={testimonial.id} testimonial={testimonial} index={i} isVisible={isVisible} />)}
          </div>
        </div>
      </div>
    </section>;
}
function TestimonialCard({
  testimonial,
  index,
  isVisible
}: {
  testimonial: {
    id: number;
    name: string;
    role: string;
    text: string;
    rating: number;
  };
  index: number;
  isVisible: boolean;
}) {
  const [animatedRating, setAnimatedRating] = useState(0);
  const hasAnimated = useRef(false);
  useEffect(() => {
    if (isVisible && !hasAnimated.current) {
      hasAnimated.current = true;
      let current = 0;
      const interval = setInterval(() => {
        current += 1;
        setAnimatedRating(current);
        if (current >= testimonial.rating) {
          clearInterval(interval);
        }
      }, 150);
      return () => clearInterval(interval);
    }
  }, [isVisible, testimonial.rating]);
  return <Card data-index={index} className="glass-effect border-white/10 hover-lift bg-white/5 flex flex-col h-full transition-all duration-500 opacity-0 translate-y-8 data-[visible=true]:opacity-100 data-[visible=true]:translate-y-0" style={{
    transitionDelay: `${index * 150}ms`
  }} data-visible={isVisible}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className="bg-secondary/20 p-3 rounded-lg">
            <Quote className="size-6 text-secondary" />
          </div>
          <div className="flex gap-1" aria-label={`Rating: ${testimonial.rating} out of 5 stars`}>
            {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`size-4 transition-all duration-500 ${star <= animatedRating ? "fill-secondary text-secondary scale-110" : "text-white/10"}`} style={{
            transitionDelay: `${star * 100}ms`
          }} />)}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="flex-grow flex flex-col justify-between space-y-6 pt-4">
        <blockquote className="text-lg leading-relaxed font-sans text-white/90 italic">
          "{testimonial.text}"
        </blockquote>

        <div className="pt-6 border-t border-white/10">
          <p className="font-display text-xl text-white font-semibold">
            {testimonial.name}
          </p>
          <p className="text-secondary text-sm uppercase tracking-widest mt-1 font-medium">
            {testimonial.role}
          </p>
        </div>
      </CardContent>
    </Card>;
}